import { Environments } from '../app/layout/model/interfaces/environments.interface';
import { View } from '../app/layout/model/interfaces/view.interface';

export const appConfig: Partial<Environments> = window.APP_CONFIG ?? {};

/**
 * Reads a string value from APP_CONFIG with a fallback default.
 * Warns if a placeholder like "__KEY__" is still present (deployment misconfiguration).
 * Never throws — the app should degrade gracefully, not crash on missing config.
 */
function readConfig(key: keyof Environments, fallback = ''): string {
  const value = appConfig[key];
  if (typeof value !== 'string') {
    return fallback;
  }
  if (value === `__${key}__`) {
    console.warn(`⚠️ CONFIG: ${key} still has placeholder value "${value}". Using fallback: "${fallback}"`);
    return fallback;
  }
  return value;
}

export function buildProdEnvironment(overrides: Partial<Environments> = {}): Environments {
  const pages = (appConfig['ENABLED_PAGES'] as string || '').split(',').map(page => page.trim()) || [];
  const base: Environments = {
    SENTRY_DSN: readConfig('SENTRY_DSN'),
    SENTRY_RELEASE: readConfig('SENTRY_RELEASE'),
    SENTRY_ENVIRONMENT: readConfig('SENTRY_ENVIRONMENT', 'development'),
    SENTRY_TRACE_RATE: readConfig('SENTRY_TRACE_RATE', '0.1'),
    SENTRY_REPLAY_RATE: readConfig('SENTRY_REPLAY_RATE', '0.1'),
    SENTRY_REPLAY_ON_ERROR_RATE: readConfig('SENTRY_REPLAY_ON_ERROR_RATE', '1.0'),
    VERSION: readConfig('VERSION', 'unknown'),
    ENABLED_PAGES: pages,
    PRODUCTION: true,
    STATIC_LOCATION: '/static/manager-studio/',
    TRANSLATIONS_LOCATION: '/assets/i18n/',
    API_URL: (appConfig.API_URL as string) || '/api/internal/',
    APP_URL: (appConfig.APP_URL as string) || '/app/',
    SUPPORT_CALL_ROUTE: 'support-form/start_support_form/',
    FONTAWESOME: { style: 'fal' },
    DEFAULT_VIEW: { route: '/dashboard', iframeUrl: 'dashboard/' } as View,
  };

  return {
    ...base,
    ...overrides,
  };
}
